import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    Label,
} from '@quadminds/mindkit';
import { Plus, X, Link, Trash2, Ban } from 'lucide-react';
import type React from 'react';

import { FC, useState } from 'react';

interface UrlItem {
    id: string;
    url: string;
    isValid: boolean;
}
interface UriListImportProps {
    isOpen: boolean;
    handleClose: (open: boolean) => void;
    setValidUris: (urls: { uri: string }[]) => void;
}
const UriListImport: FC<UriListImportProps> = ({ isOpen, handleClose, setValidUris }) => {
    const [urls, setUrls] = useState<UrlItem[]>([]);
    const [currentUrl, setCurrentUrl] = useState('');

    const validateUrl = (url: string): boolean => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const addUrl = () => {
        if (!currentUrl.trim()) return;

        const isValid = validateUrl(currentUrl);
        const newUrl: UrlItem = {
            id: Date.now().toString(),
            url: currentUrl.trim(),
            isValid,
        };

        setUrls((prev) => [...prev, newUrl]);
        setCurrentUrl('');
    };

    const removeUrl = (id: string) => {
        setUrls((prev) => prev.filter((url) => url.id !== id));
    };

    const clearAllUrls = () => {
        setUrls([]);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addUrl();
        }
    };

    const validUrls = urls.filter((url) => url.isValid);
    const invalidUrls = urls.filter((url) => !url.isValid);

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(e) => {
                handleClose(e);
                setUrls([]);
            }}
        >
            <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden dark:bg-gray-qm-900">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold font-poppins">Cargar URLs</DialogTitle>
                    <DialogDescription className="">
                        Agrega una o varias URLs para cargar en el estado de la aplicación.
                    </DialogDescription>
                </DialogHeader>
                <hr />
                <div className="space-y-4">
                    {/* Input para nueva URL */}
                    <div className="space-y-2">
                        <Label htmlFor="url-input" className="text-sm font-semibold font-poppins">
                            Nueva URL
                        </Label>
                        <div className="flex gap-2">
                            <Input
                                id="url-input"
                                placeholder="https://ejemplo.com"
                                value={currentUrl}
                                onChange={(e) => setCurrentUrl(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="flex-1 dark:bg-gray-qm-800"
                            />
                            <Button onClick={addUrl} variant="default" size="icon">
                                <Plus className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                    <hr />
                    {/* Lista de URLs */}
                    <div className="space-y-3 max-h-[300px] overflow-y-auto overflow-x-hidden">
                        {urls.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                <Link className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                <p>No hay URLs agregadas</p>
                                <p className="text-sm">Agrega tu primera URL arriba</p>
                            </div>
                        ) : (
                            <>
                                {/* URLs válidas */}
                                {validUrls.length > 0 && (
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-medium text-success flex items-center gap-2">
                                            <div className="w-2 h-2 bg-success rounded-full"></div>
                                            URLs Válidas ({validUrls.length})
                                        </h4>
                                        {validUrls.map((urlItem) => (
                                            <div
                                                key={urlItem.id}
                                                className="flex items-center justify-between gap-2 pl-2 pr-1 py-1 border border-success-200 rounded-lg w-full"
                                            >
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    <Link className="w-4 h-4 text-success flex-shrink-0" />
                                                    <span className="text-sm truncate min-w-0 flex-1 max-w-[420px]">
                                                        {urlItem.url}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeUrl(urlItem.id)}
                                                        className=""
                                                        aria-label="Remover URL"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* URLs inválidas */}
                                {invalidUrls.length > 0 && (
                                    <div className="space-y-2">
                                        <h4 className="text-sm font-medium text-error flex items-center gap-2">
                                            <div className="w-2 h-2 bg-error rounded-full"></div>
                                            URLs Inválidas ({invalidUrls.length})
                                        </h4>
                                        {invalidUrls.map((urlItem) => (
                                            <div
                                                key={urlItem.id}
                                                className="flex items-center justify-between gap-2 pl-2 pr-1 py-1 border border-error-200 rounded-lg w-full"
                                            >
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                    <Ban className="w-4 h-4 text-error flex-shrink-0" />
                                                    <span className="text-sm truncate min-w-0 flex-1 max-w-[420px]">
                                                        {urlItem.url}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeUrl(urlItem.id)}
                                                        className=""
                                                        aria-label="Remover URL"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <hr />
                <DialogFooter className="flex justify-between items-center">
                    <div className="flex gap-2">
                        {urls.length > 0 && (
                            <Button variant="ghost" onClick={clearAllUrls} className="">
                                <Trash2 className="w-4 h-4 mr-2" />
                                Borrar Todas
                            </Button>
                        )}
                        <Button
                            onClick={() => {
                                setValidUris(validUrls.map((urlItem) => ({ uri: urlItem.url })));
                                setUrls([]);
                                handleClose(false);
                            }}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            disabled={validUrls.length === 0}
                        >
                            Cargar URLs ({validUrls.length})
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UriListImport;
